///
/// EarthBrowser
///
/// Created by Matt Giger on 9/15/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Shared "entity toast" for dataset notifications (new weather alerts,
/// storm advisories): a glass toast with a tinted icon chip and a column
/// of deep-link entries that fly the camera to the entity when clicked.
/// Documented in design-system/02-components.md ("Toast Pattern").
///

import { toast } from "@/components/ui/use-toast"
import type { ToastProps } from "@/components/ui/toast"
import type { LucideIcon } from "lucide-react"

export interface EntityToastEntry {
	/** Stable key + what the click handler navigates to */
	id: string
	/** Deep-link button text, e.g. "Tornado Warning · Carson, TX" */
	label: string
	/** Optional status dot color class before the label, e.g. "bg-red-400" */
	dotClass?: string
	/** Navigate to the entity (the toast dismisses itself after) */
	onSelect: () => void
}

export interface EntityToastOptions {
	title: string
	/** Icon rendered in the tinted chip, e.g. TriangleAlert */
	icon: LucideIcon
	/** Chip tint + icon color, e.g. "bg-amber-500/15 text-amber-400" */
	iconClass: string
	/** Border/glow accent; defaults to the plain glass card */
	variant?: ToastProps["variant"]
	entries: EntityToastEntry[]
	/** Entries beyond this render as "and N more" (default 4) */
	maxEntries?: number
	/** Auto-dismiss delay in ms (default 8000) */
	duration?: number
}

const cDefaultMaxEntries = 4
const cDefaultDurationMs = 8000

export function showEntityToast(options: EntityToastOptions): void {
	if (options.entries.length === 0) return

	const shown = options.entries.slice(0, options.maxEntries ?? cDefaultMaxEntries)
	const overflow = options.entries.length - shown.length
	const Icon = options.icon

	const handle = toast({
		variant: options.variant ?? "default",
		icon: (
			<div className={`p-2 rounded-lg ${options.iconClass}`}>
				<Icon className="h-4 w-4" />
			</div>
		),
		title: options.title,
		description: (
			<div className="flex flex-col items-start gap-1.5 pt-0.5">
				{shown.map(entry => (
					<button
						key={entry.id}
						type="button"
						onClick={() => {
							entry.onSelect()
							handle.dismiss()
						}}
						className="group/entry flex items-center gap-2 text-left"
					>
						{entry.dotClass && (
							<span className={`h-2 w-2 shrink-0 rounded-full ${entry.dotClass}`} />
						)}
						<span className="underline underline-offset-2 decoration-slate-500 group-hover/entry:decoration-current transition-colors">
							{entry.label}
						</span>
					</button>
				))}
				{overflow > 0 && (
					<span className="text-slate-400">and {overflow} more</span>
				)}
			</div>
		),
		duration: options.duration ?? cDefaultDurationMs,
	})
}
